import React, { useCallback, useEffect } from 'react';
import { Formik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { account } from '@redux/account';
import { Separator } from 'components/Separator';
import { Link } from 'components/Link/Link';
import { SignInWithApple } from 'components/Button/SignInWithApple';
import { Alert } from 'components/Alert';
import { Input } from 'components/Form/Input';
import { PasswordInput } from 'components/Form/PasswordInput';
import { Button } from 'components/Button/Button';
import { LanguageDropdown } from 'components/LanguageDropdown';
import { useDispatch } from 'utils/useDispatch';
import { useQuery } from 'utils/useQuery';

export const Login = () => {
  const { t } = useTranslation();
  let navigate = useNavigate();
  const location = useLocation();
  const getParams = useQuery();
  const searchQuery = !isEmpty(getParams.toString()) ? `?${getParams.toString()}` : '';

  const token = new URLSearchParams(location.search).get('token');

  const accessToken = useSelector(account.selectors.accessToken);
  const hasError = useSelector(account.selectors.hasError);
  const firstError = useSelector(account.selectors.firstError);

  const login = useDispatch(account.actions.login);
  const adminLogin = useDispatch(account.actions.adminLogin);
  const fetchUser = useDispatch(account.actions.fetchUser);
  const setAccessToken = useDispatch(account.actions.setAccessToken);
  const clearErrors = useDispatch(account.actions.clearErrors);

  const isLocal = ['staging', 'local'].includes(process.env.REACT_APP_ENV);

  const handleNavigate = useCallback(() => {
    let { from } = location.state || { from: { pathname: "/app/playlists" } };
    navigate(from, { replace: true });
  }, [location.state, navigate]);

  useEffect(() => {
    if(!isEmpty(accessToken)) {
      setAccessToken(accessToken);
      fetchUser();
      return handleNavigate();
    }

    if(token) {
      setAccessToken(token);
      fetchUser();
      return handleNavigate();
    }
  }, [accessToken, token, setAccessToken, fetchUser, handleNavigate]);

  useEffect(() => {
    return () => clearErrors();
  }, [clearErrors]);

  const handleAdminClick = () => adminLogin();

  const handleLogin = useCallback(async (values, { setSubmitting }) => {
    await login(values);
    setSubmitting(false);
  }, [login]);

  return (
    <>
      <header className="flex flex-col space-y-5">
        <div className="relative text-center text-ui">
          <h1 className="text-xl font-semibold">{t('auth.logIn')}</h1>
          <LanguageDropdown className="absolute right-0 top-1/2 -translate-y-1/2 -mr-3 text-sm text-ui-3" />
        </div>
        {hasError && <Alert type="danger">{firstError || t('api_service.error.unknown')}</Alert>}
        <SignInWithApple />
      </header>
      <Separator className="my-6">{t('common.or')}</Separator>
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        onSubmit={handleLogin}
      >
        {({ values, handleChange, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Input
                className="w-full"
                size="small"
                type="email"
                name="email"
                autoComplete="email"
                required
                label={t('fields.email')}
                placeholder={t('fields.emailPlaceholder')}
                onChange={handleChange}
                value={values.email}
              />
            </div>
            <div>
              <PasswordInput
                className="w-full"
                size="small"
                name="password"
                autoComplete="current-password"
                required
                label={t('fields.password')}
                hint={(
                  <Link className="underline decoration-ui-3 underline-offset-4 hover:text-ui" to="/auth/reset">
                    {t('auth.forgotLink')}
                  </Link>
                )}
                placeholder={t('fields.enterPassword')}
                onChange={handleChange}
                value={values.password}
              />
            </div>
            <Button
              type="submit"
              view="cta"
              className="h-10 text-sm"
              center
              block
              isDisabled={isSubmitting}
            >{t('auth.logInButton')}</Button>
          </form>
        )}
      </Formik>
      {isLocal && (
        <Button
          view="outline"
          className="h-10 text-sm mt-3"
          center
          block
          onClick={handleAdminClick}
        >Admin Log in</Button>
      )}
      <div className="mt-8 text-center text-ui-3">
        {t('auth.signUpTitle')}
        <Link
          className="ml-2 text-ui underline decoration-ui-3 underline-offset-4 hover:decoration-ui"
          to={{
            pathname: '/auth/register',
            search: searchQuery,
          }}
        >{t('auth.signUp')}</Link>
      </div>
    </>
  );
}
